import type { RequirementEvaluation } from '@milepost/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ApiError, api, type CatalogEntry } from '../lib/api';
import './UploadDialog.css';

interface Props {
  driverId: string;
  requirement: RequirementEvaluation;
  catalogEntry: CatalogEntry | undefined;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Upload a document against one requirement.
 *
 * The dates are the point of this form, not the file. The engine acts on `expiresAt`, so
 * whoever files the document is asked for it explicitly rather than letting the system
 * assume a maximum term — a medical certificate issued for 12 months instead of 24 is
 * common, and assuming the maximum would keep an uncertified driver on the road.
 */
export function UploadDialog({ driverId, requirement, catalogEntry, onClose, onDone }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [effectiveAt, setEffectiveAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  // Escape closes, and focus starts inside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    dialogRef.current?.querySelector<HTMLElement>('input')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append('file', file!);
      form.append('requirementCode', requirement.requirementCode);
      if (effectiveAt) form.append('effectiveAt', effectiveAt);
      if (expiresAt) form.append('expiresAt', expiresAt);
      return api.postForm(`/api/drivers/${driverId}/documents`, form);
    },
    onSuccess: onDone,
  });

  const fieldErrors = submit.error instanceof ApiError ? submit.error.byField() : {};

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-title"
        ref={dialogRef}
      >
        <div className="dialog-head">
          <p className="eyebrow">{requirement.cfrCite}</p>
          <h2 id="upload-title" className="dialog-title">
            {requirement.title}
          </h2>
          {catalogEntry?.description && (
            <p className="dialog-desc prose">{catalogEntry.description}</p>
          )}
        </div>

        <form
          className="dialog-body"
          onSubmit={(e) => {
            e.preventDefault();
            if (file) submit.mutate();
          }}
        >
          <div className="field">
            <label htmlFor="upl-file">The file</label>
            <input
              id="upl-file"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/heic,image/webp,image/tiff"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              required
            />
            <p className="dialog-hint muted">
              PDF or a photo, up to 25 MB. Stored encrypted.
            </p>
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="upl-eff">Issued or executed</label>
              <input
                id="upl-eff"
                type="date"
                value={effectiveAt}
                onChange={(e) => setEffectiveAt(e.target.value)}
              />
              {fieldErrors.effectiveAt && <p className="field-error">{fieldErrors.effectiveAt}</p>}
            </div>

            <div className="field">
              <label htmlFor="upl-exp">Expires</label>
              <input
                id="upl-exp"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="dialog-hint muted">
                Copy the date printed on the document. Leave blank if it does not expire.
              </p>
              {fieldErrors.expiresAt && <p className="field-error">{fieldErrors.expiresAt}</p>}
            </div>
          </div>

          {submit.error && Object.keys(fieldErrors).length === 0 && (
            <p className="field-error">{(submit.error as Error).message}</p>
          )}

          <div className="dialog-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={!file || submit.isPending}>
              {submit.isPending ? 'Filing…' : 'File this document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
